import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from "react";
import { CoreIcon } from "./CoreIcon";
import type { CoreComposerProps, CorePrompt, CoreRepositoryRecord } from "./types";

type OpenMenu = "files" | "mentions" | "prompts" | null;

const REPOSITORY_ADD_FALLBACK = "The selected document could not be added.";

function disabledTitle(enabled: boolean, reason: string) {
  return enabled ? undefined : reason;
}

// The add handler rejects when the server refuses the document (for example a
// client-scope refusal, which returns a non-2xx and makes documentIntelligenceApi
// .addVersion throw). Discarding that rejection is what made the inline menu
// close with nothing on screen, so the reason is pulled out and shown instead.
function repositoryAddMessage(error: unknown) {
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  if (typeof error === "string" && error.trim()) return error.trim();
  return REPOSITORY_ADD_FALLBACK;
}

export function CoreComposer({
  value,
  defaultValue = "",
  placeholder = "Ask Document Intelligence…",
  attachments = [],
  submitLabel = "Send",
  submitMeta,
  busy = false,
  disabled = false,
  compact = false,
  onValueChange,
  onSubmit,
  onUploadFiles,
  onAddFromProject,
  projectFileCount,
  projectName = "Project",
  repositoryRecords = [],
  onRepositorySearch,
  onAddRepositoryRecord,
  onOpenSources,
  mentions = [],
  onMentionSelect,
  onImprove,
  improvedPrompt,
  prompts = [],
  onPromptSelect,
  onSavePrompt,
  onOpenPromptLibrary,
  promptLibraryCount,
  onDictate,
  leadingContent,
}: CoreComposerProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [repositoryOpen, setRepositoryOpen] = useState(false);
  const [repositoryQuery, setRepositoryQuery] = useState("");
  const [repositoryError, setRepositoryError] = useState<string | null>(null);
  const [repositoryPendingId, setRepositoryPendingId] = useState<string | null>(null);
  const [promptName, setPromptName] = useState("");
  const [improving, setImproving] = useState(false);
  const [suggestedPrompt, setSuggestedPrompt] = useState(improvedPrompt || "");
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLFormElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaId = useId();
  const controlled = value !== undefined;
  const prompt = controlled ? value : internalValue;
  const filesEnabled = Boolean(onUploadFiles || onAddFromProject || onAddRepositoryRecord);
  const canSubmit = Boolean(onSubmit && prompt.trim() && !busy && !disabled);

  useEffect(() => setSuggestedPrompt(improvedPrompt || ""), [improvedPrompt]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
        setRepositoryOpen(false);
        setRepositoryError(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
        setRepositoryOpen(false);
        setRepositoryError(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const updateValue = (next: string) => {
    if (!controlled) setInternalValue(next);
    onValueChange?.(next);
  };

  const handleFiles = async (files: File[]) => {
    if (!files.length || !onUploadFiles || busy || disabled) return;
    setOpenMenu(null);
    setDragging(false);
    await onUploadFiles(files);
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files || []);
    event.currentTarget.value = "";
    void handleFiles(files);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void handleFiles(Array.from(event.dataTransfer.files || []));
  };

  const improve = async () => {
    if (!onImprove || !prompt.trim()) return;
    setImproving(true);
    try {
      const next = await onImprove(prompt.trim());
      if (next) setSuggestedPrompt(next);
    } finally {
      setImproving(false);
    }
  };

  const addRepositoryRecord = async (record: CoreRepositoryRecord) => {
    if (!onAddRepositoryRecord || repositoryPendingId) return;
    setRepositoryError(null);
    setRepositoryPendingId(record.id);
    try {
      await onAddRepositoryRecord(record);
      // Success keeps the old behaviour: the menu closes behind the added row.
      setOpenMenu(null);
      setRepositoryOpen(false);
    } catch (error) {
      // Failure holds the menu open so the reason is on screen next to the row
      // that was refused, and the user can pick a different record or retry.
      setOpenMenu("files");
      setRepositoryOpen(true);
      setRepositoryError(repositoryAddMessage(error));
    } finally {
      setRepositoryPendingId(null);
    }
  };

  const selectPrompt = (item: CorePrompt) => {
    updateValue(item.prompt);
    setOpenMenu(null);
    onPromptSelect?.(item);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit) void onSubmit?.(prompt.trim());
  };

  return (
    <form
      ref={rootRef}
      className={`di-core-composer ${compact ? "di-core-composer--compact" : ""}`}
      onSubmit={submit}
    >
      {leadingContent}
      {attachments.length > 0 && (
        <div className="di-core-composer__attachments" aria-label="Attached context">
          {attachments.map((attachment) => (
            <span
              key={attachment.id}
              className={`di-core-tag di-core-tag--${attachment.kind === "playbook" ? "accent" : attachment.kind === "jurisdiction" || attachment.kind === "neutral" ? "neutral" : "outline"}`}
            >
              {attachment.kind === "file" && <CoreIcon name="file" size={12} />}
              {attachment.label}
              {attachment.onRemove && (
                <button type="button" aria-label={`Remove ${attachment.label}`} onClick={attachment.onRemove}>
                  <CoreIcon name="close" size={11} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <label className="di-core-sr-only" htmlFor={textareaId}>Ask Document Intelligence</label>
      <textarea
        id={textareaId}
        className="di-core-composer__textarea"
        value={prompt}
        rows={compact ? 2 : 4}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => {
          const next = event.currentTarget.value;
          updateValue(next);
          if (/@$/.test(next) && mentions.length && onMentionSelect) setOpenMenu("mentions");
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
          }
        }}
      />
      {suggestedPrompt && (
        <div className="di-core-composer__improvement" role="status">
          <strong>Improved prompt</strong>
          <p>{suggestedPrompt}</p>
          <p className="di-core-muted">DI added the missing context, desired output and verification instruction.</p>
          <div className="di-core-actions">
            <button type="button" className="di-core-btn di-core-btn--primary" onClick={() => {
              updateValue(suggestedPrompt);
              setSuggestedPrompt("");
            }}>
              Use this prompt
            </button>
            <button type="button" className="di-core-btn di-core-btn--secondary" onClick={() => setSuggestedPrompt("")}>
              Keep mine
            </button>
          </div>
        </div>
      )}
      <div className="di-core-composer__tools">
        <div className="di-core-menu-anchor">
          <button
            type="button"
            className="di-core-btn di-core-btn--secondary"
            aria-haspopup="menu"
            aria-expanded={openMenu === "files"}
            disabled={!filesEnabled || disabled || busy}
            title={disabledTitle(filesEnabled, "File sources are not available for this activity.")}
            onClick={() => setOpenMenu((current) => current === "files" ? null : "files")}
          >
            <CoreIcon name="paperclip" /> Files <CoreIcon name="chevron" size={12} />
          </button>
          {openMenu === "files" && (
            <div className="di-core-menu di-core-menu--files" role="menu">
              <div className="di-core-menu__kicker">Add files</div>
              {onUploadFiles && (
                <>
                  <input ref={inputRef} hidden type="file" multiple accept=".doc,.docx,.pdf,.xlsx,.pptx" onChange={handleFileInput} />
                  <div
                    className={`di-core-drop ${dragging ? "di-core-drop--active" : ""}`}
                    onDragEnter={() => setDragging(true)}
                    onDragLeave={() => setDragging(false)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleDrop}
                  >
                    <CoreIcon name="upload" size={21} />
                    <strong>Drag and drop files here</strong>
                    <span>.docx, .pdf, .xlsx, .pptx · up to 50 documents</span>
                  </div>
                  <button type="button" role="menuitem" onClick={() => inputRef.current?.click()}>
                    <CoreIcon name="folder" /> Browse this computer
                  </button>
                </>
              )}
              {onAddFromProject && (
                <button type="button" role="menuitem" onClick={() => { setOpenMenu(null); void onAddFromProject(); }}>
                  <CoreIcon name="folder" />
                  <span>Add from Project</span>
                  <small>{projectName}{projectFileCount !== undefined ? ` · ${projectFileCount}` : ""}</small>
                </button>
              )}
              {onAddRepositoryRecord && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    aria-expanded={repositoryOpen}
                    onClick={() => {
                      setRepositoryError(null);
                      setRepositoryOpen((current) => !current);
                    }}
                  >
                    <CoreIcon name="database" />
                    <span>Add from Contract Repository</span>
                    <CoreIcon name="chevron" size={12} />
                  </button>
                  {repositoryOpen && (
                    <div className="di-core-repository">
                      <label className="di-core-search">
                        <CoreIcon name="search" size={14} />
                        <span className="di-core-sr-only">Search Contract Repository</span>
                        <input
                          value={repositoryQuery}
                          placeholder="Search by counterparty or ID"
                          onChange={(event) => {
                            const query = event.currentTarget.value;
                            setRepositoryError(null);
                            setRepositoryQuery(query);
                            void onRepositorySearch?.(query);
                          }}
                        />
                      </label>
                      {repositoryError && (
                        <p
                          className="di-core-repository__error"
                          role="alert"
                          style={{ color: "var(--di-accent-700, #ae1800)", fontSize: 12, lineHeight: 1.4, margin: 12 }}
                        >
                          {repositoryError}
                        </p>
                      )}
                      {repositoryRecords.map((record) => (
                        <button
                          type="button"
                          key={record.id}
                          disabled={Boolean(repositoryPendingId) && repositoryPendingId !== record.id}
                          aria-busy={repositoryPendingId === record.id}
                          onClick={() => { void addRepositoryRecord(record); }}
                        >
                          <span><strong>{record.id}</strong> {record.counterparty}</span>
                          <small>
                            {repositoryPendingId === record.id
                              ? "Adding…"
                              : `${record.status}${record.meta ? ` · ${record.meta}` : ""}`}
                          </small>
                        </button>
                      ))}
                      {!repositoryRecords.length && <p className="di-core-menu__empty">No matching records.</p>}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="di-core-btn di-core-btn--secondary"
          disabled={!onOpenSources || disabled}
          title={disabledTitle(Boolean(onOpenSources), "Knowledge sources are not available.")}
          onClick={onOpenSources}
        >
          <CoreIcon name="database" /> Sources
        </button>

        <div className="di-core-menu-anchor">
          <button
            type="button"
            className="di-core-btn di-core-btn--secondary"
            aria-label="Mention a file or source"
            aria-haspopup="menu"
            aria-expanded={openMenu === "mentions"}
            disabled={!mentions.length || !onMentionSelect || disabled}
            title={disabledTitle(Boolean(mentions.length && onMentionSelect), "No mentionable context is available.")}
            onClick={() => setOpenMenu((current) => current === "mentions" ? null : "mentions")}
          >
            <CoreIcon name="at" /> Mention
          </button>
          {openMenu === "mentions" && (
            <div className="di-core-menu di-core-menu--mentions" role="menu">
              <div className="di-core-menu__kicker">Mention context</div>
              {mentions.map((mention) => (
                <button type="button" role="menuitem" key={mention.id} onClick={() => {
                  setOpenMenu(null);
                  if (!controlled) {
                    updateValue(/@$/.test(prompt)
                      ? `${prompt.slice(0, -1)}@${mention.label} `
                      : `${prompt}${prompt && !/\s$/.test(prompt) ? " " : ""}@${mention.label} `);
                  }
                  onMentionSelect?.(mention);
                }}>
                  <span><strong>{mention.type}</strong> {mention.label}</span>
                  {mention.detail && <small>{mention.detail}</small>}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          className="di-core-btn di-core-btn--secondary"
          disabled={!onImprove || !prompt.trim() || improving || disabled}
          title={disabledTitle(Boolean(onImprove), "Prompt improvement is not configured.")}
          onClick={() => void improve()}
        >
          <CoreIcon name="sparkle" /> {improving ? "Improving…" : "Improve"}
        </button>

        <div className="di-core-menu-anchor">
          <button
            type="button"
            className="di-core-btn di-core-btn--secondary"
            aria-haspopup="menu"
            aria-expanded={openMenu === "prompts"}
            disabled={(!prompts.length && !onSavePrompt && !onOpenPromptLibrary) || disabled}
            title={disabledTitle(Boolean(prompts.length || onSavePrompt || onOpenPromptLibrary), "Saved prompts are not available.")}
            onClick={() => setOpenMenu((current) => current === "prompts" ? null : "prompts")}
          >
            <CoreIcon name="list" /> Prompts <CoreIcon name="chevron" size={12} />
          </button>
          {openMenu === "prompts" && (
            <div className="di-core-menu di-core-menu--prompts" role="menu">
              <div className="di-core-menu__kicker">Saved prompts</div>
              {prompts.map((item) => (
                <button type="button" role="menuitem" key={item.id} onClick={() => selectPrompt(item)}>
                  <span>{item.title}</span>
                  {item.meta && <small>{item.meta}</small>}
                </button>
              ))}
              {!prompts.length && <p className="di-core-menu__empty">No saved prompts yet.</p>}
              {(onSavePrompt || onOpenPromptLibrary) && <div className="di-core-menu__footer">
                {onSavePrompt && (
                  <div className="di-core-prompt-save">
                    <label>
                      <span className="di-core-sr-only">Prompt name</span>
                      <input
                        value={promptName}
                        placeholder="Name this prompt"
                        onChange={(event) => setPromptName(event.currentTarget.value)}
                      />
                    </label>
                    <button type="button" disabled={!prompt.trim() || !promptName.trim()} onClick={() => {
                      const title = promptName.trim();
                      setOpenMenu(null);
                      setPromptName("");
                      void onSavePrompt(prompt.trim(), title);
                    }}>
                      Save prompt
                    </button>
                  </div>
                )}
                {onOpenPromptLibrary && (
                  <button type="button" onClick={() => { setOpenMenu(null); onOpenPromptLibrary(); }}>
                    All {promptLibraryCount ?? prompts.length}
                  </button>
                )}
              </div>}
            </div>
          )}
        </div>

        <span className="di-core-composer__spacer" />
        {submitMeta && <span className="di-core-composer__meta">{submitMeta}</span>}
        <button
          type="button"
          className="di-core-btn di-core-btn--secondary di-core-btn--icon"
          aria-label="Dictate"
          disabled={!onDictate || disabled}
          title={disabledTitle(Boolean(onDictate), "Dictation is not configured.")}
          onClick={() => void onDictate?.()}
        >
          <CoreIcon name="mic" />
        </button>
        <button type="submit" className="di-core-btn di-core-btn--primary" disabled={!canSubmit}>
          {busy ? "Working…" : submitLabel} <CoreIcon name="arrow" />
        </button>
      </div>
    </form>
  );
}
